/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { ActionRequestProgMes } from "../../../model/ActionRequestProgMes";
import { Filter } from '../../../model/Filter';
import { BaseController } from '../../BaseController';
import { CloseViewController } from './CloseView';
import { CurativeActionViewController } from './CurativeActionView';

class ActionRequestProgMesViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, actionRequestProgMesService, actionRequestServiceLevelService, actionRequestStateBeforeService, actionRequestStateAfterService, actionRequestSymptomService, equipmentService, equipmentTemplateService, alarmService,
        companyService, accountService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService, $scope,
        dialogService, authenticationService, causeService, filterService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.actionRequestProgMesService = actionRequestProgMesService;
        this.actionRequestServiceLevelService = actionRequestServiceLevelService;
        this.actionRequestStateBeforeService = actionRequestStateBeforeService;
        this.actionRequestStateAfterService = actionRequestStateAfterService;
        this.actionRequestSymptomService = actionRequestSymptomService;
        this.equipmentService = equipmentService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.alarmService = alarmService;
        this.companyService = companyService;
        this.accountService = accountService;
        this.authenticationService = authenticationService;
        this.causeService = causeService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$log.debug('Controller \'ActionRequestView\' loaded');
    }

    $doCheck() {
        if (this.actionRequestProgMes) {
            if (!angular.equals(this.actionRequestProgMes, this.pristineEntity)) {
                this.viewHeaderDisabled.cancelDisabled = true;
                this.viewHeaderDisabled.completeDisabled = true;
            } else {
                if (this.actionRequestView.$valid) {
                    this.viewHeaderDisabled.cancelDisabled = false;
                    this.viewHeaderDisabled.completeDisabled = false;
                } else {
                    this.viewHeaderDisabled.cancelDisabled = true;
                    this.viewHeaderDisabled.completeDisabled = true;
                }
            }
        }
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.accountsDisabled = false;
        this.alarms = [];
        this.selectedAlarms = [];
        this.commands = [];
        this.viewHeaderDisabled = [];
        this.actionRequestView = [];

        // The updateMode flag can be used to know the context in which this controller is running: creation or update.
        let initPromise;
        let equipmentsPromise;
        let companiesPromise;
        if (this.authenticationService.userHasRole(['ADMIN', 'OPERATION_MANAGER'])) {
            companiesPromise = this.companyService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('enabled', true)])
            });
        } else {
            companiesPromise = [];
        }
        let selectedEquipmentPromise;
        let accountsPromise;
        if (this.$stateParams.hasOwnProperty('id')) {
            this.updateMode = true;
            this.title = 'ui.actionrequestprogmes.update';
            initPromise = this.actionRequestProgMesService.getEntity(this.$stateParams.id);
            equipmentsPromise = [];
            if (this.authenticationService.userHasRole(['ADMIN', 'OPERATION_MANAGER'])) {
                accountsPromise = initPromise.then(entity => this.getAccounts(entity.delegateeCompanyId));
            } else {
                accountsPromise = [];
            }
        } else {
            this.updateMode = false;
            this.title = 'ui.actionrequestprogmes.creation';
            initPromise = this.$q.resolve(false);
            equipmentsPromise = this.equipmentService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            });
            if (this.$stateParams.equipmentId) {
                // If equipment is preselected, the list of accounts shall be preloaded and contain accounts in the
                // delegatee company of the equipment.
                selectedEquipmentPromise = this.equipmentService.getEntity(this.$stateParams.equipmentId);
                accountsPromise = selectedEquipmentPromise.then(
                    equipment => this.getAccounts(equipment.delegateeCompanyId)
                );
            } else {
                accountsPromise = [];
            }
        }

        this.handleRestServiceCall(this.$q.all({
            entity: initPromise,
            equipments: equipmentsPromise,
            companies: companiesPromise,
            accounts: accountsPromise,
            selectedEquipment: selectedEquipmentPromise,
        }), data => {
                this.pristineEntity = new ActionRequestProgMes(data.entity);
                this.equipments = data.equipments;
                this.companies = data.companies;
                this.accounts = data.accounts;

                if (data.selectedEquipment) {
                    this.selectedEquipment = data.selectedEquipment;
                    this.pristineEntity.equipmentId = data.selectedEquipment.id;
                    this.pristineEntity.delegateeCompanyId = data.selectedEquipment.delegateeCompanyId;
                }
                this.reset();
                this.initCommands();
            }
        ).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        if (this.updateMode) {
            switch (this.actionRequestProgMes.status) {
            case 'OPENED':
                this.commands.push(
                    Command.handlerCommand('ui.actionrequest.complete', 'images/svg/flag.svg',
                        () => this.showStatusUpdateConfirmationDialog('COMPLETED'),
                        ['ADMIN', 'ACTION_REQUEST_COMPLETE'], "completeDisabled"),
                    Command.handlerCommand('ui.actionrequest.cancel', 'images/svg/cancel.svg',
                        () => this.showStatusUpdateConfirmationDialog('CANCELED'), ['ADMIN', 'ACTION_REQUEST_WRITE']
                        ,  "cancelDisabled")
                );
                break;
            case 'COMPLETED':
                this.commands.push(
                    Command.handlerCommand('ui.actionrequest.reopen', 'images/svg/wrench.svg',
                        () => this.showStatusUpdateConfirmationDialog('OPENED'), ['ADMIN', 'ACTION_REQUEST_WRITE']),
                    Command.handlerCommand('ui.actionrequest.close', 'images/svg/checked.svg',
                        () => this.showStatusUpdateConfirmationDialog('CLOSED'), ['ADMIN', 'ACTION_REQUEST_WRITE']),
                    Command.handlerCommand('ui.actionrequest.cancel', 'images/svg/cancel.svg',
                        () => this.showStatusUpdateConfirmationDialog('CANCELED'), ['ADMIN', 'ACTION_REQUEST_WRITE'])
                );
                break;
            default:
                break;
            }
        }
    }

    /**
     * Loads alarms pending on an equipment.
     *
     * @param {Number} [equipmentId] Equipment ID.
     */
    loadDelegateeCompanyAndAlarms() {
        if (this.selectedEquipment && !this.updateMode) {
            this.actionRequestProgMes.equipmentId = this.selectedEquipment.id;
            this.actionRequestProgMes.delegateeCompanyId = this.selectedEquipment.delegateeCompanyId;
            let accountsPromise;
            if (this.actionRequestProgMes.delegateeCompanyId) {
                accountsPromise = this.getAccounts(this.actionRequestProgMes.delegateeCompanyId);
            }  else {
                accountsPromise = [];
            }
            this.accountsDisabled = true;
            this.handleRestServiceCall(this.$q.all({
                accounts: accountsPromise,
                alarms: this.getAlarms(this.actionRequestProgMes.equipmentId)
            }), data => {
                this.accounts = data.accounts;
                this.loadAlarms(data.alarms);
            }).catch(() => {
                this.notificationService.pushMessage('ui.actionrequest.alarmloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.accountsDisabled = false;
            });
        }
    }

    /**
     * Loads accounts in a company.
     *
     * @param {Number} [companyId] Company ID.
     */
    loadAccounts(companyId) {
        if (companyId) {
            this.accountsDisabled = true;
            this.handleRestServiceCall(
                this.getAccounts(companyId),
                accounts => this.accounts = accounts
            ).catch(() => {
                this.notificationService.pushMessage('ui.actionrequest.userloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.accountsDisabled = false;
            });
        }
    }

    /**
     * Gets accounts in a company.
     *
     * @param {Number} companyId Company ID.
     * @return {Promise} Promise containing the accounts loaded.
     */
    getAccounts(companyId) {
        return this.accountService.findEntities({
            data: this.filterService.buildFilters([
                Filter.equalFilter('enabled', true),
                Filter.equalFilter('system', false),
                Filter.equalFilter('company.id', companyId)
            ])
        });
    }

    /**
     * Shows a confirmation dialog before changing the status. If the dialog is confirmed, the action request' status is
     * updated.
     *
     * @param {status} New status.
     * @return {Promise} A promise of the action.
     */
    showStatusUpdateConfirmationDialog(status) {
        let messageKey;
        switch (status) {
        case 'CANCELED':
            messageKey = 'ui.actionrequest.cancelconfirmation';
            break;
        case 'OPENED':
            messageKey = 'ui.actionrequest.reopenconfirmation';
            break;
        case 'COMPLETED':
            messageKey = 'ui.actionrequest.completeconfirmation';
            break;
        case 'CLOSED':
            messageKey = 'ui.actionrequestprogmes.closeconfirmation';
            break;
        default:
            break;
        }

        let promise;
        if (messageKey && this.updateMode) {
            promise = this.dialogService.showConfirmationDialogWithMessage(messageKey).then(confirmed => {
                if (confirmed) {
                    this.updateStatus(status);
                }
            });
        } else {
            promise = this.$q.reject(false);
        }
        return promise;
    }

    /**
     * Converts this form data to valid DTO (form multiparts).
     *
     * @return {Object} Form multiparts, indexed by name.
     */
    convertToDto() {
        const formData = {
            actionRequestProgMes: new Blob([angular.toJson(this.actionRequestProgMes)], {type: 'application/json'}),
            attachment: this.attachment
        }
        return formData;
    }

    save() {
        this.formDisabled = true;
        let serviceActionPromise;
        if (this.updateMode) {
            serviceActionPromise = this.actionRequestProgMesService.updateEntityWithUpload({id: this.actionRequestProgMes.id }, this.convertToDto());
        } else {
            serviceActionPromise = this.actionRequestProgMesService.createEntityWithUpload(this.convertToDto());
        }
        this.handleRestServiceCall(serviceActionPromise, () => {
            const message = 'ui.actionrequest.' + (this.updateMode ? 'updated' : 'created');
            this.notificationService.pushMessage(message, this.configuration.NOTIFIER_LEVEL_INFO);
            if (this.updateMode) {
                this.$state.reload();
            } else {
                this.$state.go('gmao.actionrequest.list');
            }
        }).finally(() => {
            this.formDisabled = false;
        });
    }

    updateStatus(status) {
        if (this.updateMode) {
            this.formDisabled = true;
            let serviceActionPromise;
            switch (status) {
            case 'OPENED':
                serviceActionPromise = this.actionRequestProgMesService.reopen(this.actionRequestProgMes.id);
                break;
            case 'COMPLETED':
                serviceActionPromise = this.actionRequestProgMesService.complete(this.actionRequestProgMes.id);
                break;
            case 'CLOSED':
                serviceActionPromise = this.actionRequestProgMesService.close(this.actionRequestProgMes.id);
                break;
            case 'CANCELED':
                serviceActionPromise = this.actionRequestProgMesService.cancel(this.actionRequestProgMes.id);
                break;
            default:
                serviceActionPromise = this.$q.resolve(true);
                break;
            }

            this.handleRestServiceCall(serviceActionPromise, () => {
                this.notificationService.pushMessage('ui.actionrequest.updated',
                    this.configuration.NOTIFIER_LEVEL_INFO);
                this.$state.reload();
            }).finally(() => {
                this.formDisabled = false;
            });
        }
    }

    /**
     * Restores the initial state of the view, and the map.
     */
    reset() {
        this.actionRequestProgMes = angular.copy(this.pristineEntity);
    }
}

export const actionRequestProgMesViewComponent = {
    template: require('./actionrequestprogmes-view.html'),
    controller: ActionRequestProgMesViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/actionrequest/component/ActionRequestProgMesView.js